import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { recipes } from '../data/recipes';

/* ------------------------------------------------------------------ *
 * "IA" SIMULÉE (maquette) — heuristique locale sur les recettes en dur.
 * Démontre la valeur : budget, ingrédients, occasion, substitutions.
 * ------------------------------------------------------------------ */

const OCCASIONS = ['weekend', 'anniversaire', 'fête', 'fete', 'rentrée', 'rentree', 'picnic', 'pique-nique'];
const CATEGORIES = [
  { k: 'petit-déjeuner', words: ['petit-déjeuner', 'petit dejeuner', 'petit-déj', 'matin', 'déjeuner'] },
  { k: 'dessert', words: ['dessert', 'gâteau', 'gateau'] },
  { k: 'goûter', words: ['goûter', 'gouter', 'enfants', 'collation'] },
  { k: 'apéritif', words: ['apéritif', 'aperitif', 'apéro'] },
];
const SUBSTITUTIONS = {
  beurre: 'le **beurre** → même quantité d’huile neutre, ou de la margarine.',
  œuf: 'un **œuf** → ½ banane écrasée ou 3 c. à s. de compote de pommes.',
  oeuf: 'un **œuf** → ½ banane écrasée ou 3 c. à s. de compote de pommes.',
  lait: 'le **lait** → lait végétal (soja, coco) ou eau + un peu d’huile.',
  sucre: 'le **sucre** → miel ou sirop (réduis un peu le liquide).',
  farine: 'la **farine** de blé → farine de maïs/riz (texture un peu différente).',
};

const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// mots-clés d'ingrédients dérivés des recettes
const INGREDIENT_WORDS = Array.from(
  new Set(
    recipes
      .flatMap((r) => (r.ingredients || []).map((i) => i.name))
      .map((n) => norm(n))
      .filter((w) => w.length > 3)
  )
);

function extractBudget(text) {
  const m = norm(text).match(/(\d[\d\s.]{1,7})\s*(f|fcfa|franc)/);
  let n = m ? Number(m[1].replace(/[\s.]/g, '')) : null;
  if (!n) {
    const bare = norm(text).match(/\b(\d{3,6})\b/);
    n = bare ? Number(bare[1]) : null;
  }
  return n && n >= 200 ? n : null;
}

function buildAnswer(text) {
  const t = norm(text);

  // --- intention substitution ---
  if (/(remplac|a la place|sans |je n'?ai pas)/.test(t)) {
    const found = Object.keys(SUBSTITUTIONS).find((k) => t.includes(norm(k)));
    if (found) {
      return {
        text: `Pas de souci 👩‍🍳 Tu peux remplacer ${SUBSTITUTIONS[found]}\n\nDis-moi la recette et ton budget, je t’en propose une adaptée !`,
        recipes: [],
      };
    }
  }

  const budget = extractBudget(t);
  const occasion = OCCASIONS.find((o) => t.includes(norm(o)));
  const category = CATEGORIES.find((c) => c.words.some((w) => t.includes(norm(w))))?.k;
  const ingredients = INGREDIENT_WORDS.filter((w) => t.includes(w));

  let list = recipes.slice();
  if (budget) list = list.filter((r) => r.estimated_cost <= budget);
  if (category) list = list.filter((r) => r.category === category);
  if (occasion) {
    const o = norm(occasion);
    list = list.filter((r) => (r.occasions || []).some((x) => norm(x).includes(o.slice(0, 5))));
  }
  if (ingredients.length) {
    list = list.filter((r) => {
      const hay = norm(`${r.name} ${r.description} ${(r.ingredients || []).map((i) => i.name).join(' ')}`);
      return ingredients.some((w) => hay.includes(w));
    });
  }

  list = list.sort((a, b) => b.rating * b.votes - a.rating * a.votes).slice(0, 3);

  // message contextuel
  const bits = [];
  if (budget) bits.push(`budget de ${budget.toLocaleString('fr-FR')} FCFA`);
  if (ingredients.length) bits.push(`avec ${ingredients.slice(0, 3).join(', ')}`);
  if (occasion) bits.push(`pour ${occasion}`);
  if (category) bits.push(category);

  if (list.length === 0) {
    return {
      text: budget
        ? `Hmm, je ne trouve rien sous ${budget.toLocaleString('fr-FR')} FCFA avec ces critères. Essaie d’augmenter un peu le budget 🙂`
        : `Je n’ai pas trouvé de recette correspondante. Donne-moi un budget (ex. « 2000 FCFA ») ou un ingrédient (ex. « banane ») !`,
      recipes: [],
    };
  }

  const intro = bits.length
    ? `Voici mes recettes Tartina ${bits.join(' ')} 👇`
    : `Voici quelques idées gourmandes à la Tartina 👇`;
  return { text: intro, recipes: list };
}

const SUGGESTIONS = [
  "J'ai 2000 FCFA pour le goûter",
  'Un dessert pour un anniversaire',
  'Une recette avec des bananes',
  'Je remplace le beurre par quoi ?',
];

const greeting = {
  from: 'bot',
  text: "Salut ! 👋 Je suis l’assistant Tartina. Dis-moi ton budget, tes ingrédients ou l’occasion, et je te trouve la recette parfaite.",
  recipes: [],
};

/* ------------------------------------------------------------------ */

const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open]);

  const send = (raw) => {
    const text = (raw ?? input).trim();
    if (!text || typing) return;
    setMessages((m) => [...m, { from: 'user', text }]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      setMessages((m) => [...m, { from: 'bot', ...buildAnswer(text) }]);
      setTyping(false);
    }, 650);
  };

  return (
    <>
      <Fab
        as={motion.button}
        onClick={() => setOpen((v) => !v)}
        aria-label="Assistant Tartina"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
      >
        <i className={open ? 'ti ti-x' : 'ti ti-message-chatbot'} aria-hidden="true" />
        {!open && <Ping />}
      </Fab>

      <AnimatePresence>
        {open && (
          <Panel
            as={motion.div}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <PanelHead>
              <Avatar><i className="ti ti-chef-hat" aria-hidden="true" /></Avatar>
              <div>
                <b>Assistant Tartina</b>
                <small><Dot /> en ligne · IA</small>
              </div>
              <Close onClick={() => setOpen(false)} aria-label="Fermer"><i className="ti ti-x" /></Close>
            </PanelHead>

            <Body ref={bodyRef}>
              {messages.map((m, i) => (
                <Msg key={i} $me={m.from === 'user'}>
                  <Bubble $me={m.from === 'user'}>
                    {m.text.split('\n').map((line, j) => (
                      <span key={j}>{renderBold(line)}<br /></span>
                    ))}
                  </Bubble>
                  {m.recipes?.length > 0 && (
                    <Results>
                      {m.recipes.map((r) => (
                        <ResultCard key={r.id} to={`/recipes/${r.id}`} onClick={() => setOpen(false)}>
                          <img src={r.image} alt="" />
                          <div>
                            <b>{r.name}</b>
                            <small>{r.estimated_cost.toLocaleString('fr-FR')} FCFA · ★ {r.rating.toFixed(1)}</small>
                          </div>
                          <i className="ti ti-chevron-right" aria-hidden="true" />
                        </ResultCard>
                      ))}
                    </Results>
                  )}
                </Msg>
              ))}

              {typing && (
                <Msg $me={false}>
                  <Bubble $me={false}><Typing><span /><span /><span /></Typing></Bubble>
                </Msg>
              )}

              {messages.length <= 1 && (
                <Chips>
                  {SUGGESTIONS.map((s) => (
                    <Chip key={s} onClick={() => send(s)}>{s}</Chip>
                  ))}
                </Chips>
              )}
            </Body>

            <InputBar
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ton budget, un ingrédient…"
                aria-label="Message"
              />
              <button type="submit" aria-label="Envoyer" disabled={!input.trim()}>
                <i className="ti ti-send" aria-hidden="true" />
              </button>
            </InputBar>
            <Disclaimer>Assistant de démonstration · suggestions à titre indicatif</Disclaimer>
          </Panel>
        )}
      </AnimatePresence>
    </>
  );
};

// rend les **gras** markdown simples
function renderBold(line) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

/* ----------------------------- styles ----------------------------- */
const Fab = styled.button`
  position: fixed; right: 22px; bottom: 22px; z-index: 1000;
  width: 60px; height: 60px; border-radius: 50%; cursor: pointer; border: none;
  background: ${({ theme }) => theme.colors.primary}; color: #fff;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: flex; align-items: center; justify-content: center;
  i { font-size: 1.7rem; }
`;
const Ping = styled.span`
  position: absolute; top: 12px; right: 12px; width: 12px; height: 12px; border-radius: 50%;
  background: ${({ theme }) => theme.colors.secondary}; border: 2px solid ${({ theme }) => theme.colors.primary};
`;
const Panel = styled.div`
  position: fixed; right: 22px; bottom: 92px; z-index: 1000;
  width: 370px; max-width: calc(100vw - 32px); height: 540px; max-height: calc(100vh - 130px);
  background: ${({ theme }) => theme.colors.background};
  border-radius: 22px; overflow: hidden; display: flex; flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  @media (max-width: 480px) { right: 12px; left: 12px; width: auto; bottom: 86px; }
`;
const PanelHead = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 14px 16px;
  background: ${({ theme }) => theme.colors.chocolate}; color: #fff;
  b { font-family: ${({ theme }) => theme.fonts.heading}; font-size: 1rem; display: block; }
  small { display: flex; align-items: center; gap: 5px; font-size: 0.74rem; opacity: 0.85; }
`;
const Avatar = styled.div`
  width: 38px; height: 38px; border-radius: 50%; flex: none;
  background: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.chocolate};
  display: flex; align-items: center; justify-content: center; i { font-size: 1.3rem; }
`;
const Dot = styled.span`width: 7px; height: 7px; border-radius: 50%; background: #4ade80;`;
const Close = styled.button`
  margin-left: auto; background: transparent; border: none; color: #fff; cursor: pointer; font-size: 1.3rem;
`;
const Body = styled.div`
  flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px;
`;
const Msg = styled.div`
  display: flex; flex-direction: column; align-items: ${({ $me }) => ($me ? 'flex-end' : 'flex-start')};
`;
const Bubble = styled.div`
  max-width: 85%; padding: 10px 14px; border-radius: 16px; font-size: 0.92rem; line-height: 1.5;
  background: ${({ $me, theme }) => ($me ? theme.colors.primary : '#fff')};
  color: ${({ $me, theme }) => ($me ? '#fff' : theme.colors.textPrimary)};
  border: 1px solid ${({ $me, theme }) => ($me ? 'transparent' : theme.colors.border)};
  border-bottom-right-radius: ${({ $me }) => ($me ? '4px' : '16px')};
  border-bottom-left-radius: ${({ $me }) => ($me ? '16px' : '4px')};
  br:last-child { display: none; }
  strong { color: ${({ $me, theme }) => ($me ? '#fff' : theme.colors.primary)}; }
`;
const Results = styled.div`display: flex; flex-direction: column; gap: 8px; margin-top: 8px; width: 100%;`;
const ResultCard = styled(Link)`
  display: flex; align-items: center; gap: 10px; text-decoration: none;
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 14px; padding: 8px;
  transition: all .15s ease;
  img { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; flex: none; }
  b { display: block; font-size: 0.88rem; color: ${({ theme }) => theme.colors.chocolate}; }
  small { font-size: 0.76rem; color: ${({ theme }) => theme.colors.textSecondary}; }
  i { margin-left: auto; color: ${({ theme }) => theme.colors.secondaryDark}; }
  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; transform: translateX(2px); }
`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px;`;
const Chip = styled.button`
  cursor: pointer; border: 1px solid ${({ theme }) => theme.colors.secondaryDark};
  background: ${({ theme }) => theme.colors.cream}; color: ${({ theme }) => theme.colors.chocolate};
  font-weight: 700; font-size: 0.8rem; padding: 7px 12px; border-radius: 999px; font-family: inherit;
  &:hover { background: ${({ theme }) => theme.colors.secondary}; }
`;
const Typing = styled.div`
  display: flex; gap: 4px;
  span { width: 7px; height: 7px; border-radius: 50%; background: ${({ theme }) => theme.colors.chocolateMilk};
    animation: blink 1.2s infinite both; }
  span:nth-child(2) { animation-delay: .2s; }
  span:nth-child(3) { animation-delay: .4s; }
  @keyframes blink { 0%, 80%, 100% { opacity: .3; } 40% { opacity: 1; } }
`;
const InputBar = styled.form`
  display: flex; gap: 8px; padding: 10px 12px 4px; background: ${({ theme }) => theme.colors.background};
  input {
    flex: 1; border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 999px;
    padding: 11px 16px; font-size: 0.92rem; font-family: inherit; outline: none; background: #fff;
  }
  input:focus { border-color: ${({ theme }) => theme.colors.secondaryDark}; }
  button {
    flex: none; width: 44px; height: 44px; border-radius: 50%; border: none; cursor: pointer;
    background: ${({ theme }) => theme.colors.primary}; color: #fff; font-size: 1.2rem;
    display: flex; align-items: center; justify-content: center;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;
const Disclaimer = styled.p`
  margin: 0; text-align: center; font-size: 0.68rem; color: ${({ theme }) => theme.colors.textSecondary};
  padding: 4px 8px 10px;
`;

export default ChatAssistant;
